import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';

import { Checkbox, HStack, VStack } from '@rbilabs/universal-components';
import * as Notifications from 'expo-notifications';
import { NotificationPermissionsStatus } from 'expo-notifications';
import { useIntl } from 'react-intl';
import { AppState, AppStateStatus, NativeEventSubscription } from 'react-native';

import ConfirmDialog from 'components/confirm-dialog';
import { InstallAppButton } from 'components/install-app-button';
import LoadingAnimation from 'components/loading-animation';
import { useLoyaltyQuestQuery } from 'components/quests/loyalty-quest/use-loyalty-quest-query';
import { UserJourneyFormLabel } from 'components/quests/user-jouney-form-label';
import { toaster } from 'components/toaster/toaster';
import { TextInput } from 'components/ucl/text-input';
import { OptInLabel } from 'components/user-info-form/styled';
import { IUserInfoSubmitData } from 'components/user-info-form/types';
import {
  CommunicationPreferenceType,
  IsoCountryCode,
  LoyaltyCmsType,
  useUpdateUserCommunicationPreferencesMutation,
} from 'generated/graphql-gateway';
import useIsComponentMounted from 'hooks/use-is-component-mounted';
import { routes } from 'navigation/routing';
import {
  UserJourneyFields,
  UserJourneySections,
  getUserJourneyStepsForSection,
} from 'pages/loyalty/loyalty-user-journey/utils';
import { useAuthContext } from 'state/auth';
import { useLocale } from 'state/intl';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useLoyaltyUserId } from 'state/loyalty/hooks/use-loyalty-user';
import { usePushPermission } from 'state/push-permissions';
import { isNative, isWeb } from 'utils/environment';
import { getPhoneNumberErrorMessage, isPhoneNumberValid, sanitizePhoneNumber } from 'utils/form';
import { Keys } from 'utils/local-storage/constants';
import { WebLocalStorage } from 'utils/local-storage/web-local-storage';
import logger from 'utils/logger';
import { openPushSetting } from 'utils/permissions';

import {
  CommPreferenceDescription,
  CommPreferenceLabel,
  InputContainer,
  LoadingToggleContainer,
  PermissionsContainer,
  StyledOptInLink,
  ToggleButton,
  ToggleContainer,
  ToggleDescription,
  ToggleLabel,
  ToggleLabelContainer,
} from './styled';

export const SMS_SUBSCRIPTION_EXP_TIME = 3;
export const EMAIL_SUBSCRIPTION_EXP_TIME = 3;
export const PUSH_SUBSCRIPTION_EXP_TIME = 3;

interface ICommCardEmailPush {
  marketingEmail: boolean;
  marketingPush: boolean;
  marketingSms: boolean;
  setMarketingEmailPrefs: React.Dispatch<React.SetStateAction<boolean>>;
  setPushPrefs: React.Dispatch<React.SetStateAction<boolean>>;
  setMarketingSmsPrefs: React.Dispatch<React.SetStateAction<boolean>>;
  userPhoneNumber?: string;
  setIsNewPhoneNumberValid: Dispatch<SetStateAction<boolean>>;
  setOnUpdatePhoneNumber: Dispatch<SetStateAction<(() => Promise<void>) | undefined>>;
}

const ToggleButtonWithLoading = ({
  label,
  description,
  isDescriptionDisabled,
  isLoading,
  children,
  hideBottomBorder,
}: {
  children: React.ReactNode;
  label: React.ReactNode;
  description?: string;
  isDescriptionDisabled?: boolean;
  isLoading?: boolean;
  hideBottomBorder?: boolean;
}) => {
  return (
    <ToggleContainer hideBottomBorder={hideBottomBorder} alignItems="center" space={4}>
      <ToggleLabelContainer>
        <ToggleLabel>{label}</ToggleLabel>
        {description && (
          <ToggleDescription isDisabled={!!isDescriptionDisabled}>{description}</ToggleDescription>
        )}
      </ToggleLabelContainer>
      {isLoading ? (
        <LoadingToggleContainer>
          <LoadingAnimation size="sm" />
        </LoadingToggleContainer>
      ) : (
        children
      )}
    </ToggleContainer>
  );
};

const CommCardEmailPush: React.FC<ICommCardEmailPush> = ({
  marketingEmail,
  marketingPush,
  marketingSms,
  setMarketingEmailPrefs,
  setPushPrefs,
  setMarketingSmsPrefs,
  userPhoneNumber,
  setOnUpdatePhoneNumber,
  setIsNewPhoneNumberValid,
}: ICommCardEmailPush) => {
  const { requestPermission } = usePushPermission();
  const { formatMessage } = useIntl();
  const { user, updateUserInfo } = useAuthContext();
  const smsSubscriptionEnabled = useFlag(LaunchDarklyFlag.ENABLE_SMS_MARKETING_SUBSCRIPTIONS);
  const [notificationPermissionsStatus, setNotificationPermissionsStatus] = useState<
    NotificationPermissionsStatus
  >();
  const [showPushDialog, setShowPushDialog] = useState(false);
  const [newPhoneNumber, setNewPhoneNumber] = useState('');
  const [acceptMarketingSMSCommPrefs, setAcceptMarketingSMSCommPrefs] = useState(false);
  const [privacyPolicyChecked, setPrivacyPolicyChecked] = useState(false);
  const isMounted = useIsComponentMounted();
  const [phoneNumberError, setPhoneNumberError] = useState('');
  const [touchedPhoneNumber, setTouchedPhoneNumber] = useState(false);
  const [isSubmittingPhoneNumber, setIsSubmittingPhoneNumber] = useState(false);
  const { region } = useLocale();

  const [
    updateUserCommunicationPreferencesMutation,
  ] = useUpdateUserCommunicationPreferencesMutation();

  const { loyaltyUserId } = useLoyaltyUserId();
  const { quest: challenge } = useLoyaltyQuestQuery({
    loyaltyId: loyaltyUserId,
    cmsType: LoyaltyCmsType.USER_JOURNEY,
  });

  const challengeSteps =
    (challenge && getUserJourneyStepsForSection(challenge, UserJourneySections.Communications)) ||
    {};

  /**
   * Updates user preferences in Braze/DynamoDB
   */
  const changeSubscriptionStatus = async (
    communicationPreferenceType: CommunicationPreferenceType,
    newStatus: boolean
  ) => {
    try {
      const { data } = await updateUserCommunicationPreferencesMutation({
        variables: {
          input: {
            communicationPreferenceType,
            newStatus,
          },
        },
      });
      switch (communicationPreferenceType) {
        case CommunicationPreferenceType.MARKETINGEMAIL:
          setMarketingEmailPrefs(!!data?.updateUserCommunicationPreferences.marketingEmail);
          WebLocalStorage.setItemWithExpiration(
            Keys.MARKETING_EMAIL_SUBSCRIPTION_REQUESTED,
            newStatus,
            EMAIL_SUBSCRIPTION_EXP_TIME
          );
          break;
        case CommunicationPreferenceType.MARKETINGPUSH:
          setPushPrefs(!!data?.updateUserCommunicationPreferences.marketingPush);
          WebLocalStorage.setItemWithExpiration(
            Keys.MARKETING_PUSH_SUBSCRIPTION_REQUESTED,
            newStatus,
            PUSH_SUBSCRIPTION_EXP_TIME
          );
          break;
        case CommunicationPreferenceType.MARKETINGSMS:
          setMarketingSmsPrefs(!!data?.updateUserCommunicationPreferences.marketingSms);
          // We store this value in local storage to keep displaying the toggle as ON in case the user
          // exits and re-enters the Preference Center without having validated the subscription by
          // sending an SMS from their device..
          if (newStatus) {
            toaster.positive(formatMessage({ id: 'smsSubscriptionConfirmation' }));
          }

          WebLocalStorage.setItemWithExpiration(
            Keys.MARKETING_SMS_SUBSCRIPTION_REQUESTED,
            newStatus,
            SMS_SUBSCRIPTION_EXP_TIME
          );
          break;
        default:
          break;
      }
    } catch (error) {
      logger.error({
        error,
        message: `Failed to update ${communicationPreferenceType} subscription status`,
      });
      toaster.negative(formatMessage({ id: 'smsSubscriptionUpdateError' }));
    }
  };

  /**
   * Compares the device permissions and the Braze permissions.
   * If Braze's marketingEmail is true, but the user doesn't have Push Notifications enabled,
   * a popup will be prompted to change the device preferences, asking user for Push
   * Notification permissions.
   */
  const checkPushPermissions = async (pushPermissionsStatus: boolean) => {
    if (!isNative) {
      return;
    }

    const pushPermissionsGranted = !!notificationPermissionsStatus?.granted;

    // If Braze is true and device is false: Ask user for permissions
    if (pushPermissionsStatus && !pushPermissionsGranted) {
      if (notificationPermissionsStatus?.canAskAgain) {
        const settings = await requestPermission();
        setNotificationPermissionsStatus(settings);
        if (settings.granted) {
          changeSubscriptionStatus(CommunicationPreferenceType.MARKETINGPUSH, true);
        }
      } else {
        setShowPushDialog(true);
      }
    }
  };

  /**
   * Gets the status of the Push Notification permissions from the user's device
   */
  const loadDeviceSettings = async (updateBraze: boolean) => {
    const settings = await Notifications.getPermissionsAsync();
    setNotificationPermissionsStatus(settings);
    if (updateBraze && settings.granted) {
      handlePushNotificationToggle(true);
    }
  };

  const handleAppStateChange = (nextAppState: AppStateStatus) => {
    if (nextAppState === 'active') {
      // The app has returned from the background (e.g., from device settings)
      // update the device settings
      loadDeviceSettings(true);
    }
  };

  const handlePushNotificationToggle = async (isChecked: boolean) => {
    await changeSubscriptionStatus(CommunicationPreferenceType.MARKETINGPUSH, isChecked);
  };

  const closePushNotificationsDialog = () => {
    setShowPushDialog(false);
  };

  const handleSmsSubscription = async (isChecked: boolean) => {
    if (!marketingSms && isChecked && (!acceptMarketingSMSCommPrefs || !privacyPolicyChecked)) {
      toaster.negative(formatMessage({ id: 'agreeToTheTerms' }));
      return;
    }
    setMarketingSmsPrefs(isChecked);

    await changeSubscriptionStatus(CommunicationPreferenceType.MARKETINGSMS, isChecked);
  };

  const handleUpdatePhoneNumber = async (phoneNumber: string) => {
    if (!user?.details) {
      return;
    }
    setIsSubmittingPhoneNumber(true);
    // Save the new phone number
    const isoCountryCode = (user?.details?.isoCountryCode as IsoCountryCode) || IsoCountryCode.USA;
    const formattedPhoneNumber = await sanitizePhoneNumber(phoneNumber, isoCountryCode);

    const userForm: IUserInfoSubmitData = {
      ...user?.details,
      adsPersonalizationOptOut: user?.details.adsPersonalizationOptOut ?? false,
      agreesToTermsOfService: true,
      isoCountryCode,
      phoneNumber: formattedPhoneNumber,
    };
    await updateUserInfo(userForm);

    // Set the SMS toggle to false
    await changeSubscriptionStatus(CommunicationPreferenceType.MARKETINGSMS, false);

    setAcceptMarketingSMSCommPrefs(false);
    setPrivacyPolicyChecked(false);

    setIsNewPhoneNumberValid(false);
    setIsSubmittingPhoneNumber(false);
  };

  const getFormErrors = async ({ phoneNumber }: { phoneNumber: string }) => {
    const phoneNumberValidation = phoneNumber
      ? await isPhoneNumberValid({
          phoneNumber,
          country: region,
        })
      : { valid: true };

    if (!phoneNumberValidation.valid) {
      setPhoneNumberError(getPhoneNumberErrorMessage(phoneNumberValidation.error, formatMessage));
    } else {
      setPhoneNumberError('');
    }

    /**
     * A user is allowed to update their phone number when:
     * - The new number is valid and different from the one stored in the user's profile.
     * - The phone number is empty (removal is allowed).
     */
    const canUpdatePhoneNumber =
      (!!phoneNumber && phoneNumber !== userPhoneNumber && phoneNumberValidation.valid) ||
      !phoneNumber;

    setIsNewPhoneNumberValid(canUpdatePhoneNumber);
  };

  useEffect(() => {
    setOnUpdatePhoneNumber(() => () => handleUpdatePhoneNumber(newPhoneNumber));
  }, [newPhoneNumber, setOnUpdatePhoneNumber]);

  useEffect(() => {
    if (marketingPush && !!notificationPermissionsStatus) {
      checkPushPermissions(marketingPush);
    }
  }, [marketingPush, notificationPermissionsStatus]);

  useEffect(() => {
    let subscription: NativeEventSubscription;
    if (isNative && isMounted) {
      loadDeviceSettings(false);

      subscription = AppState.addEventListener('change', handleAppStateChange);
    }
    return () => subscription?.remove();
  }, [isNative, isMounted]);

  useEffect(() => {
    setNewPhoneNumber(userPhoneNumber ?? '');
  }, [userPhoneNumber]);

  return (
    <>
      <VStack justifyContent="space-between" alignItems="flex-start">
        <CommPreferenceLabel>
          {formatMessage({ id: 'emailPushPreferencesTitle' })}
        </CommPreferenceLabel>
        <CommPreferenceDescription>
          {formatMessage({ id: 'emailPushPreferencesDescription' })}
        </CommPreferenceDescription>

        {/* Email Subscription Button */}
        <ToggleButtonWithLoading
          label={
            challengeSteps[UserJourneyFields.Email] ? (
              <UserJourneyFormLabel
                label={formatMessage({ id: 'email' })}
                step={challengeSteps[UserJourneyFields.Email]}
                fieldName={UserJourneyFields.Email}
                fieldType="boolean"
                inline
              />
            ) : (
              formatMessage({ id: 'email' })
            )
          }
        >
          <ToggleButton
            aria-label="marketing email switch button"
            name="CommEmail"
            isChecked={marketingEmail}
            onValueChange={isChecked => {
              setMarketingEmailPrefs(isChecked);
              changeSubscriptionStatus(CommunicationPreferenceType.MARKETINGEMAIL, isChecked);
            }}
            testID="CommEmail-marketing-switch"
          />
        </ToggleButtonWithLoading>

        <ToggleButtonWithLoading
          label={
            challengeSteps[UserJourneyFields.PushNotification] ? (
              <UserJourneyFormLabel
                label={formatMessage({ id: 'push' })}
                step={challengeSteps[UserJourneyFields.PushNotification]}
                fieldName={UserJourneyFields.PushNotification}
                fieldType="boolean"
                inline
              />
            ) : (
              formatMessage({ id: 'push' })
            )
          }
        >
          {isNative && (
            <ToggleButton
              aria-label="push notifications switch button"
              name="CommMobile"
              isChecked={marketingPush}
              onValueChange={isChecked => {
                setPushPrefs(isChecked);
                if (isChecked && !notificationPermissionsStatus?.granted && isNative) {
                  checkPushPermissions(isChecked);
                  return;
                }
                handlePushNotificationToggle(isChecked);
              }}
              testID="CommMobile-marketing-push"
            />
          )}
          {isWeb && <InstallAppButton />}
        </ToggleButtonWithLoading>

        {/* SMS Subscription Button */}
        {smsSubscriptionEnabled && (
          <InputContainer>
            <TextInput
              noMargin
              labelProps={{ marginTop: '$4' }}
              accessibilityLabel={formatMessage({ id: 'phoneNumber' })}
              onChangeText={(phoneNumber: string) => {
                getFormErrors({ phoneNumber });
                setNewPhoneNumber(phoneNumber.trim());
              }}
              onBlur={() => {
                setTouchedPhoneNumber(true);
              }}
              label={formatMessage({ id: 'phoneNumber' })}
              value={newPhoneNumber}
              placeholder={formatMessage({ id: 'phoneNumber' })}
              testID="sms-phone-number"
              errorMessage={touchedPhoneNumber ? phoneNumberError : ''}
              isDisabled={isSubmittingPhoneNumber}
            />
            <ToggleButtonWithLoading
              hideBottomBorder={true}
              label={
                challengeSteps[UserJourneyFields.PhoneNumber] ? (
                  <UserJourneyFormLabel
                    label={formatMessage({ id: 'sms' })}
                    step={challengeSteps[UserJourneyFields.PhoneNumber]}
                    fieldName={UserJourneyFields.PhoneNumber}
                    fieldType="boolean"
                    inline
                  />
                ) : (
                  formatMessage({ id: 'sms' })
                )
              }
            >
              <ToggleButton
                aria-label="SMS marketing subscription toggle button"
                isChecked={marketingSms}
                name="CommSMS"
                onValueChange={handleSmsSubscription}
                testID="CommSMS-marketing-switch"
                disabled={
                  isSubmittingPhoneNumber || userPhoneNumber !== newPhoneNumber || !userPhoneNumber
                }
              />
            </ToggleButtonWithLoading>
            {!marketingSms && (
              <PermissionsContainer>
                <HStack alignItems="top" mb="$6">
                  <Checkbox
                    value={'accept-marketing-sms-comm-prefs'}
                    aria-label={formatMessage({ id: 'acceptMarketingSMSCommPrefs' })}
                    isChecked={acceptMarketingSMSCommPrefs}
                    onChange={checked => {
                      setAcceptMarketingSMSCommPrefs(checked);
                    }}
                  />
                  <OptInLabel>{formatMessage({ id: 'acceptMarketingSMSCommPrefs' })}</OptInLabel>
                </HStack>
                <HStack alignItems="top" mb="$2">
                  <Checkbox
                    value={'accept-privacy-policy'}
                    aria-label={formatMessage({ id: 'acceptTermsAndPrivacyCommPrefs' })}
                    isChecked={privacyPolicyChecked}
                    onChange={checked => {
                      setPrivacyPolicyChecked(checked);
                    }}
                  />
                  <OptInLabel>
                    {formatMessage(
                      { id: 'acceptTermsAndPrivacyCommPrefs' },
                      {
                        bkRoyalPerksTextProgramLink: (
                          <StyledOptInLink to={routes.textTerms}>
                            {formatMessage({ id: 'textTerms' })}
                          </StyledOptInLink>
                        ),
                        privacyPolicyLink: (
                          <StyledOptInLink to={routes.privacyPolicy}>
                            {formatMessage({ id: 'privacyPolicy' })}
                          </StyledOptInLink>
                        ),
                        rewardsPolicyLink: (
                          <StyledOptInLink to={routes.termsOfService}>
                            {formatMessage({ id: 'termsOfService' })}
                          </StyledOptInLink>
                        ),
                      }
                    )}
                  </OptInLabel>
                </HStack>
              </PermissionsContainer>
            )}
          </InputContainer>
        )}
      </VStack>
      <ConfirmDialog
        showDialog={showPushDialog}
        heading={formatMessage({ id: 'push' })}
        body={formatMessage({ id: 'enablePushNotificationsOnDevice' })}
        cancelLabel={formatMessage({ id: 'cancel' })}
        confirmLabel={formatMessage({ id: 'openDeviceSettings' })}
        onDismiss={closePushNotificationsDialog}
        onCancel={closePushNotificationsDialog}
        onConfirm={() => {
          closePushNotificationsDialog();
          openPushSetting();
        }}
        modalAppearanceEventMessage="Confirm Open device Push Notification settings"
      />
    </>
  );
};

export default CommCardEmailPush;
